import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";

import { CartContext } from "../../context/CartContext";
import api from "../../api/axios";

// Import subcomponents
import SearchBox from "./SearchBox";
import UserMenu from "./UserMenu";
import HeaderIcons from "./HeaderIcons";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount } = useContext(CartContext);

  const [query, setQuery] = useState("");
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [vendorLogo, setVendorLogo] = useState(null);

  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem("user");
    const parsed = saved ? JSON.parse(saved) : null;
    setUser(parsed);
    // Reset menu state when component mounts
    setMenuOpen(false);
    setAnchorEl(null);
  }, [location.pathname]);

  const role = user?.role || null;
  const avatarSrc = vendorLogo || user?.avatar;

  const dashboardRoute = role
    ? role === "admin"
      ? "/admin"
      : role === "vendor"
      ? "/vendor/dashboard"
      : role === "agent"
      ? "/agent"
      : "/customer"
    : "/login";

  // Fetch vendor logo if user is a vendor
  useEffect(() => {
    const fetchVendorLogo = async () => {
      if (role === "vendor" && user?._id) {
        try {
          // Try multiple possible endpoints and response structures
          try {
            const res = await api.get("/vendor/profile");
            if (res.data?.storeLogo) {
              setVendorLogo(res.data.storeLogo);
              return;
            }
            if (res.data?.logo) {
              setVendorLogo(res.data.logo);
              return;
            }
            if (res.data?.avatar) {
              setVendorLogo(res.data.avatar);
              return;
            }
          } catch (err) {
            console.error("Failed to fetch vendor profile", err);
          }
          
          // Fallback to store endpoint
          try {
            const res = await api.get(`/vendor/${user._id}/store`);
            if (res.data?.vendor?.storeLogo) {
              setVendorLogo(res.data.vendor.storeLogo);
              return;
            }
            if (res.data?.vendor?.logo) {
              setVendorLogo(res.data.vendor.logo);
              return;
            }
          } catch (err) {
            console.error("Failed to fetch vendor store", err);
          }
        } catch (err) {
          console.error("Failed to fetch vendor logo", err);
        }
      }
    };
    fetchVendorLogo();
  }, [role, user]);

  // Debounced search suggestions
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim().length >= 2) {
        api
          .get(`/search/suggest?q=${query}`)
          .then((res) => {
            setSuggestions(res.data.products || []);
            setCategories(res.data.categories || []);
            setShowDropdown(true);
          })
          .catch((err) => {
            console.error("Search suggestion error", err);
            setSuggestions([]);
            setCategories([]);
          });
      } else {
        setSuggestions([]);
        setCategories([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleClick = (e) => {
    if (!showDropdown) return;
    const isInsideDropdown = e.target.closest('[role="presentation"]');
    if (!isInsideDropdown) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [showDropdown]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    navigate("/login");
  };

  const getProfileRoute = (role) => {
    switch (role) {
      case "customer":
        return "/customer/dashboard";
      case "vendor":
        return "/register/vendor";
      case "agent":
        return "/agent";
      case "admin":
        return "/admin";
      default:
        return "/login";
    }
  };

  const getRegistrationRoute = (role) => {
    switch (role) {
      case "customer":
        return "/register/customer";
      case "vendor":
        return "/register/vendor";
      case "agent":
        return "/register/agent";
      case "admin":
        return "/register/admin";
      default:
        return "/login";
    }
  };

  return (
    <AppBar position="fixed" sx={{ bgcolor: "#131921", zIndex: 1300 }}>
      {/* ================= ROW 1 ================= */}
      <Toolbar
        sx={{
          minHeight: 64,
          display: "flex",
          gap: 2,
        }}
      >
        {/* LOGO */}
        <Typography
          component={Link}
          to="/"
          sx={{
            color: "#fff",
            textDecoration: "none",
            fontWeight: 700,
            fontSize: 20,
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "#aaa", marginRight: 6 }}>Pepper</span>Diamond
        </Typography>

        {/* 🔍 SEARCH (DESKTOP ONLY) */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
          }}
        >
          <SearchBox
            query={query}
            setQuery={setQuery}
            suggestions={suggestions}
            categories={categories}
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
          />
        </Box>

        {/* ICONS */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
          <HeaderIcons
            user={user}
            role={role}
            dashboardRoute={dashboardRoute}
            cartCount={cartCount}
          />
          <UserMenu
            user={user}
            role={role}
            avatarSrc={avatarSrc}
            anchorEl={anchorEl}
            menuOpen={menuOpen}
            setAnchorEl={setAnchorEl}
            setMenuOpen={setMenuOpen}
            handleLogout={handleLogout}
            getProfileRoute={getProfileRoute}
            navigate={navigate}
          />
        </Box>
      </Toolbar>

      {/* ================= ROW 2 (MOBILE/TABLET SEARCH) ================= */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          px: 2,
          pb: 1.5,
          justifyContent: "center",
        }}
      >
        <SearchBox
          query={query}
          setQuery={setQuery}
          suggestions={suggestions}
          categories={categories}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
        />
      </Box>
    </AppBar>
  );
};

export default Header;
